import { Component, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { LayoutService } from '@services/layout/layout.service';
import { SharedModule } from '@shared/modules/shared.module';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppSidebarComponent } from './menu/sidebar/sidebar.component';

@Component({
	selector: 'app-layout',
	imports: [SharedModule],
	templateUrl: './layout.component.html',
	styleUrl: './layout.component.scss',
})
export class AppLayoutComponent implements OnDestroy {
	overlayMenuOpenSubscription: Subscription;
	menuOutsideClickListener: any;
	profileMenuOutsideClickListener: any;

	@ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

	constructor(
		public layoutService: LayoutService,
		public renderer: Renderer2,
		public router: Router,
		public authService: AuthService,
	) {
		this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
			this.setupMenuOutsideClickListener();

			this.handleStaticMenuMobileActive();
		});

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
			this.hideMenu();
			this.hideProfileMenu();
		});
	}

	private setupMenuOutsideClickListener(): void {
		if (!this.menuOutsideClickListener) {
			this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
				if (this.isMenuOutsideClicked(event)) {
					this.hideMenu();
				}
			});
		}
	}

	private handleStaticMenuMobileActive(): void {
		if (this.layoutService.state.staticMenuMobileActive) {
			this.blockBodyScroll();
		}
	}

	private isMenuOutsideClicked(event: MouseEvent): boolean {
		return !(
			this.appSidebar.el.nativeElement.isSameNode(event.target) ||
			this.appSidebar.el.nativeElement.contains(event.target)
		);
	}

	hideMenu() {
		this.layoutService.state.overlayMenuActive = false;
		this.layoutService.state.staticMenuMobileActive = false;
		this.layoutService.state.menuHoverActive = false;
		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener();
			this.menuOutsideClickListener = null;
		}
		this.unblockBodyScroll();
	}

	hideProfileMenu() {
		this.layoutService.state.profileSidebarVisible = false;
		if (this.profileMenuOutsideClickListener) {
			this.profileMenuOutsideClickListener();
			this.profileMenuOutsideClickListener = null;
		}
	}

	blockBodyScroll(): void {
		if (document.body.classList) {
			document.body.classList.add('blocked-scroll');
		} else {
			document.body.className += ' blocked-scroll';
		}
	}

	unblockBodyScroll(): void {
		if (document.body.classList) {
			document.body.classList.remove('blocked-scroll');
		} else {
			document.body.className = document.body.className.replace(
				new RegExp(`(^|\\b)${'blocked-scroll'.split(' ').join('|')}(\\b|$)`, 'gi'),
				' ',
			);
		}
	}

	get containerClass() {
		return {
			'layout-theme-light': this.layoutService.config.colorScheme === 'light',
			'layout-theme-dark': this.layoutService.config.colorScheme === 'dark',
			'layout-overlay': this.layoutService.config.menuMode === 'overlay',
			'layout-static': this.layoutService.config.menuMode === 'static',
			'layout-static-inactive':
				this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config.menuMode === 'static',
			'layout-overlay-active': this.layoutService.state.overlayMenuActive,
			'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
			'p-input-filled': this.layoutService.config.inputStyle === 'filled',
			'p-ripple-disabled': !this.layoutService.config.ripple,
		};
	}

	ngOnDestroy() {
		if (this.overlayMenuOpenSubscription) {
			this.overlayMenuOpenSubscription.unsubscribe();
		}

		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener();
		}

		if (this.profileMenuOutsideClickListener) {
			this.profileMenuOutsideClickListener();
		}
	}
}
